import React, { Component, ReactNode } from 'react';
import PageLoader from './PageLoader';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  resetKey?: string; // Add resetKey for stable route-based resets
}

interface State {
  hasError: boolean;
}

class SuspenseErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    console.error('[SuspenseErrorBoundary] Caught error:', error);
    console.error('[SuspenseErrorBoundary] Error details:', {
      message: error.message,
      name: error.name,
      stack: error.stack?.split('\n').slice(0, 5).join('\n'),
      timestamp: new Date().toISOString(),
      location: window.location.pathname,
      isReactError185: error.message?.includes('185'),
      isSuspenseError: error.message?.includes('suspend')
    });
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error details with more context
    const componentStackLines = errorInfo.componentStack?.split('\n').slice(0, 10).join('\n');
    
    console.error('[SuspenseErrorBoundary] componentDidCatch:', {
      error: error.message,
      errorType: error.name,
      componentStack: componentStackLines,
      errorBoundaryProps: this.props.resetKey,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      isProduction: process.env.NODE_ENV === 'production'
    });
    
    // Special handling for React Error #185
    if (error.message?.includes('185')) {
      console.error('[SuspenseErrorBoundary] React Error #185 detected - Component suspended during synchronous update', {
        hint: 'This usually happens when a lazy-loaded component suspends without proper Suspense boundary',
        possibleCauses: [
          'A hook inside the lazy component is suspending',
          'TanStack Query using suspense mode',
          'Missing Suspense boundary between lazy load and suspending component'
        ]
      });
    }

    // Attempt one-time hard reload on asset/chunk load errors to recover after deploys
    // Covers Webpack (ChunkLoadError) and native ESM/Vite-style dynamic import failures
    const msg = error?.message || '';
    const name = (error as any)?.name || '';
    const isRecoverableAssetError =
      name === 'ChunkLoadError' ||
      /Loading chunk .* failed/i.test(msg) ||
      /Failed to fetch dynamically imported module/i.test(msg) ||
      /Expected a (JavaScript|module) .* MIME type/i.test(msg) ||
      /Importing a module script failed/i.test(msg) ||
      /script has an unsupported MIME type/i.test(msg);

    if (isRecoverableAssetError) {
      const key = 'chunk-reload-attempted';
      try {
        const attempted = sessionStorage.getItem(key);
        if (!attempted) {
          sessionStorage.setItem(key, '1');
          // Force reload to fetch the latest asset manifest
          window.location.reload();
        }
      } catch (_) {
        // If sessionStorage is not available, still try to reload once
        window.location.reload();
      }
    }
  }

  componentDidUpdate(prevProps: Props) {
    // Only reset error state when resetKey changes (actual route change)
    // This prevents the meta-loop caused by children reference changes
    if (this.state.hasError && this.props.resetKey && prevProps.resetKey !== this.props.resetKey) {
      console.log('[SuspenseErrorBoundary] Resetting error state due to route change', {
        prevKey: prevProps.resetKey,
        newKey: this.props.resetKey
      });
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      // Don't auto-reset, wait for navigation or user action
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Something went wrong</h2>
          <p>Please refresh the page or try again later.</p>
          <button 
            onClick={() => {
              // Prefer a hard reload to recover from asset mismatches
              try {
                window.location.reload();
              } catch (_e) {
                // As a fallback, try to reset boundary state
                this.setState({ hasError: false });
              }
            }}
            style={{
              padding: '10px 20px',
              marginTop: '10px',
              background: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Reload
          </button>
        </div>
      );
    }

    // Avoid wrapping children in Suspense here to prevent nested Suspense
    // AppRoutes already wraps lazy content in its own Suspense fallback
    return <>{this.props.children}</>;
  }
}

export default SuspenseErrorBoundary;
